import type { Repo } from '../data'
import type { Snapshot } from '../data/snapshotTypes'
import { fetchAllEvents, fromServerEvent } from '../data/live'
import type { Game, ID, StatEvent } from '../domain/types'

/**
 * Load the seeded staging season into the local store for a signed-out guest
 * (task 04, staging only).
 *
 * A guest has no session and no local data, so the interactive Stats screen --
 * which reads local events -- would be empty. This populates the local store
 * from what an anonymous staging visitor is allowed to read: the games from the
 * public snapshot, and the events from the RLS-gated stat_events table. It runs
 * only in staging guest mode (behind VITE_STAGING and no session), so it never
 * touches a real user's data.
 *
 * Nothing here writes to the server; it only fills the guest's own browser.
 */
export async function loadGuestData(repo: Repo, snapshot: Snapshot, seasonId: ID): Promise<void> {
  const now = Date.now()

  // Games we scored: FINAL fixtures. Server ids, so events line up (03.1(2)).
  const games: Game[] = snapshot.games
    .filter((g) => g.status === 'FINAL')
    .map((g) => ({
      id: g.id,
      seasonId,
      date: g.date,
      playhqGameId: g.playhq_game_id,
      opponentId: null,
      opponentName: g.opponent_name ?? 'Opponent',
      roundName: g.round_name,
      roundAbbreviation: g.round_abbreviation,
      venueName: g.venue_name,
      courtName: g.court_name,
      isFinalRound: g.is_final_round,
      countsTowardLadder: g.counts_toward_ladder,
      homeAway: g.home_away,
      roster: [],
      ourScore: g.our_score,
      theirScore: g.their_score,
      status: 'final',
      currentPeriod: 1,
      notes: '',
      createdAt: now,
      updatedAt: now,
    }))

  const res = await fetchAllEvents()
  const events: StatEvent[] = res.ok ? res.data.map(fromServerEvent) : []

  // Each game's roster is whoever it has events for, so the grid has rows.
  const rosterByGame = new Map<string, Set<ID>>()
  for (const e of events) {
    if (!rosterByGame.has(e.gameId)) rosterByGame.set(e.gameId, new Set())
    rosterByGame.get(e.gameId)!.add(e.playerId)
  }
  for (const g of games) {
    g.roster = [...(rosterByGame.get(g.id) ?? [])]
    await repo.saveGame(g)
  }
  if (events.length > 0) await repo.saveEvents(events)
}
